/**
 * Interface for the assignment item in the response
 */
export interface Assignment {
  _id: string
  text: string
  category: string
  order: number
  enterprise_id: string
}


/**
 * Interface for the display in the agency-displays response
 */
export interface AgencyDisplay {
  display: number
  screen_url: string
  screen_id: string
  active?: boolean
}

/**
 * Interface for the server in the agency-displays response
 */
export interface AgencyServer {
  server_id?: string
  name?: string
  polling_seconds?: number
  license_key?: string
  active?: boolean
  displays: AgencyDisplay[]
}

/**
 * Interface for the agency schedule
 */
export interface AgencySchedule {
  startTime: string
  endTime: string
  timezone: string
}

/**
 * Interface for the agency in the agency-displays response
 */
export interface AgencyData {
  _id: string
  name: string
  active?: boolean
  schedule: AgencySchedule
  enterprise_id: string
  servers: AgencyServer[]
}

/**
 * Interface for the agency-displays response
 */
export interface AgencyDisplayResponse {
  success: boolean
  data: AgencyData[]
}

/**
 * Error class for assignment related errors
 */
export class AssignmentError extends Error {
  code: string

  constructor(message: string, code: string) {
    super(message)
    this.name = 'AssignmentError'
    this.code = code
  }
}

/**
 * Interface for a display in the upsert request
 */
export interface UpsertDisplay {
  display: number
  screen_url: string
  screen_id: string
  active?: boolean
}

/**
 * Interface for a server in the upsert request
 */
export interface UpsertServer {
  server_id?: string
  name?: string
  screen_id?: string
  screen_url?: string
  polling_seconds?: number
  license_key?: string
  active?: boolean
  displays: UpsertDisplay[]
}

/**
 * Request body for the agency-displays/upsert endpoint
 */
export interface UpsertAgencyDisplaysRequest {
  agency_id: string
  enterprise_id: string
  name?: string
  active?: boolean
  schedule?: AgencySchedule
  servers: UpsertServer[]
}

/**
 * Response from the agency-displays/upsert endpoint
 */
export interface UpsertAgencyDisplaysResponse {
  success: boolean
  agency_created: number
  agency_updated: number
  servers_created: number
  servers_updated: number
  displays_created: number
  displays_updated: number
  message: string
}
/**
 * Interface for the display detail in the response
 */
export interface ScreenDetail {
  _id: string
  name: string
  assignments: string[]
  url: string
}

/**
 * Interface for the enterprise info in screen-display-detail
 */
export interface EnterpriseInfo {
  schedule: AgencySchedule
  enterprise_id: string
  enterprise_name: string
  base_url: string
}

/**
 * Interface for the screen-display-detail response data
 */
export interface ScreenDisplayDetailData {
  enterprise: EnterpriseInfo
  assignments: Assignment[]
  screens: ScreenDetail[]
}

/**
 * Interface for the screen-display-detail response
 */
export interface ScreenDisplayDetailResponse {
  success: boolean
  data: ScreenDisplayDetailData
}

/**
 * Request body for the screens/upsert endpoint
 */
export interface UpsertScreenRequest {
  _id?: string
  name: string
  enterprise_id: string
  assignments: string[]
}

/**
 * Response from the screens/upsert endpoint
 */
export interface UpsertScreenResponse {
  success: boolean
  updated: number
  inserted_id: string | number
  message: string
}
